import { useState, useEffect } from "react";
import { Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import type { SortOption, SortSelection } from "@src/features/clips/types/camera";
import { CAMERA_SORT_OPTIONS, toggleSortOption } from "@src/features/cameras/utils/camera-sort-config";

interface SortDropdownProps {
  value: SortSelection;
  onChange: (value: SortSelection) => void;
}

export function SortDropdown({ value, onChange }: SortDropdownProps) {
  const { theme } = useUnistyles();
  const [isOpen, setIsOpen] = useState(false);

  const selectedCount = value.length;
  const hasActiveSort = selectedCount > 0;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (option: SortOption) => {
    onChange(toggleSortOption(value, option));
  };

  const handleClearSort = () => {
    onChange([]);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside (web)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest("[data-sort-dropdown]")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  return (
    <View style={{ position: "relative" }} data-sort-dropdown>
      {/* Sort Button */}
      <Pressable
        onPress={handleToggle}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: theme.spacing.md,
          paddingHorizontal: theme.spacing["3xl"],
          paddingVertical: theme.spacing.lg,
          borderWidth: 1,
          borderColor: isOpen ? theme.colors.blue[500] : theme.colors.border,
          borderRadius: sizes.radius,
          backgroundColor: theme.colors.surface,
          height: 44,
          minWidth: 100,
          position: "relative",
        }}
      >
        <Text textStyle="sm/regular" color="text">
          Sort
        </Text>
        <Icon name="sort" size={theme.spacing["4xl"]} color={theme.colors.text} />

        {/* Badge */}
        {hasActiveSort && (
          <View
            style={{
              position: "absolute",
              top: -8,
              right: -8,
              width: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: theme.colors.blue[500],
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text textStyle="xs/semibold" style={{ color: theme.colors.white.default }}>
              {selectedCount}
            </Text>
          </View>
        )}
      </Pressable>

      {/* Dropdown Menu */}
      {isOpen && (
        <View
          style={{
            position: "absolute",
            top: 48,
            right: 0,
            minWidth: 160,
            backgroundColor: theme.colors.surface,
            borderRadius: sizes.radius,
            borderWidth: 1,
            borderColor: theme.colors.border,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            elevation: 8,
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          {/* Sort Options */}
          {CAMERA_SORT_OPTIONS.map((option) => {
            const isSelected = value.includes(option.value);
            return (
              <Pressable
                key={option.value}
                onPress={() => handleSelect(option.value)}
                style={({ hovered }: { hovered?: boolean }) => ({
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingVertical: theme.spacing["3xl"],
                  paddingHorizontal: theme.spacing["4xl"],
                  backgroundColor: hovered ? theme.colors.muted : "transparent",
                })}
              >
                <Text textStyle={isSelected ? "sm/semibold" : "sm/regular"} color="text">
                  {option.label}
                </Text>
                <Icon
                  name="checkmark"
                  size={theme.spacing["4xl"]}
                  color={isSelected ? theme.colors.blue[500] : "transparent"}
                />
              </Pressable>
            );
          })}

          {/* Divider */}
          <View
            style={{
              height: 1,
              backgroundColor: theme.colors.border,
              marginHorizontal: theme.spacing["3xl"],
            }}
          />

          {/* Clear Sort */}
          <Pressable
            onPress={handleClearSort}
            style={({ hovered }: { hovered?: boolean }) => ({
              flexDirection: "row",
              alignItems: "center",
              paddingVertical: theme.spacing["3xl"],
              paddingHorizontal: theme.spacing["4xl"],
              backgroundColor: hovered ? theme.colors.muted : "transparent",
            })}
          >
            <Text textStyle="sm/regular" color="textMuted">
              Clear sort
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}
